import { Injectable, inject } from '@angular/core';
import { ENUM_AdviserPortalTheme } from '@api-new/common';
import { UserApiService } from '@api-new/userservice';
import { ThemeService } from '@app/shared/services/theme.service';
import { environment } from '@environments/environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ErrorModel } from '@shared-lib/models/error.model';
import { FullstoryService } from '@shared-lib/services/fullstory.service';
import { PermissionEnum } from '@shared/enums/permission.enum';
import { PlatformTypeEnum } from '@shared/enums/platform-type.enum';
import { PermissionService } from '@shared/services/permission.service';
import {
  loadAdvisorUserData,
  loadAdvisorUserDataFailure,
  loadAdvisorUserDataSuccess,
  setInitialPlatformType,
  setPlatformType,
} from '@shared/store/platform/platform.actions';
import { getUserById, getUserSuccess } from '@shared/store/user/user.action';
import { companyIdentifier } from '@shared/utils/company-identifier';
import { getDisplayName } from '@shared/utils/get-display-name';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

const LOCAL_STORAGE_PLATFORM_TYPE_KEY = 'PLATFORM_TYPE';

@Injectable()
export class PlatformEffect {
  private readonly actions$ = inject(Actions);
  private readonly permissionService = inject(PermissionService);
  private readonly userApiService = inject(UserApiService);
  private readonly themeService = inject(ThemeService);
  private readonly fullstoryService = inject(FullstoryService);

  loadAdvisorUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvisorUserData),
      switchMap(() =>
        this.userApiService.HTTP_AP_LoadAdvisorUserData().pipe(
          map((response) => loadAdvisorUserDataSuccess({ response })),
          catchError((error: ErrorModel) => of(loadAdvisorUserDataFailure({ error }))),
        ),
      ),
    ),
  );

  handleLoadAdvisorUserDataSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvisorUserDataSuccess),
      tap(({ response }) => {
        const theme = this.mapAdviserPortalThemeToClassName(response.company?.adviserPortalTheme);
        this.themeService.setTheme(theme);
      }),
      map(({ response }) => getUserById({ id: response.advisorUser.id })),
    ),
  );

  identifyUser = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getUserSuccess),
        tap(({ user }) => {
          this.fullstoryService.identify(user.id, {
            displayName: getDisplayName(user),
            email: user.email,
            companyIdentifier: companyIdentifier(
              { id: user.companyId, name: user.companyTradingName },
              this.permissionService.hasCurrentUserRequiredPermissions([{ permission: PermissionEnum.SystemAdmin }]),
            ),
          });
        }),
      ),
    { dispatch: false },
  );

  setInitialPlatformType = createEffect(() =>
    this.actions$.pipe(
      ofType(setInitialPlatformType),
      map(() => {
        if (environment.production) {
          return setPlatformType({ platformType: PlatformTypeEnum.Adviser });
        } else {
          const localStorageValue = localStorage.getItem(LOCAL_STORAGE_PLATFORM_TYPE_KEY) as PlatformTypeEnum;
          return setPlatformType({ platformType: localStorageValue ? localStorageValue : PlatformTypeEnum.Adviser });
        }
      }),
    ),
  );

  setPlatformType = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setPlatformType),
        tap(({ platformType }) => {
          if (!environment.production) {
            localStorage.setItem(LOCAL_STORAGE_PLATFORM_TYPE_KEY, platformType);
          }
        }),
      ),
    { dispatch: false },
  );

  private mapAdviserPortalThemeToClassName(theme: ENUM_AdviserPortalTheme | undefined): string {
    switch (theme) {
      case ENUM_AdviserPortalTheme.ADVISER_PORTAL_THEME_MAB:
        return 'theme-mab';
      case ENUM_AdviserPortalTheme.ADVISER_PORTAL_THEME_DASHLY:
      case ENUM_AdviserPortalTheme.ADVISER_PORTAL_THEME_UNSPECIFIED:
      default:
        return 'theme-dashly';
    }
  }
}
